import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, TrendingDown, Clock, CheckCircle2 } from 'lucide-react';
import { oh4Stats, oh4Features } from '../data/mockData';

const OH4 = () => {
  return (
    <div className="page">
      {/* Hero Section */}
      <section className="page-hero hero-with-image">
        <div className="hero-background-image">
          <img 
            src="https://images.unsplash.com/photo-1521618755572-156ae0cdd74d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxzdXN0YWluYWJsZSUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzYzMjE4NDQxfDA&ixlib=rb-4.1.0&q=85"
            alt="Solar energy technology"
            loading="eager"
          />
          <div className="hero-overlay"></div>
        </div>
        <div className="container">
          <div className="page-hero-content">
            <div className="badge">Heat Transfer Technology</div>
            <h1 className="heading-1">OH4 Heat Transfer System</h1>
            <p className="body-large">
              Revolutionary frequency programming tape that improves heat transfer in closed water heating systems. Reduce fuel consumption by 13-31% with zero maintenance. Works universally on any make or model.
            </p>
            <Link to="/contact" className="btn-primary">
              Request Information
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="section bg-section">
        <div className="container">
          <div className="stats-grid">
            {oh4Stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
                <p className="stat-description">{stat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">How OH4 Works</h2>
            <p className="section-subtitle">
              Advanced optimization algorithms that continuously adjust your systems for maximum efficiency.
            </p>
          </div>

          <div className="features-list">
            <div className="feature-list-item">
              <div className="feature-icon">
                <Cpu size={32} />
              </div>
              <div className="feature-content">
                <h3 className="heading-3">Frequency Programming</h3>
                <p className="body-medium">
                  OH4 tape transfers frequency programming to the water in your heating system pipework, causing water molecules to group together more efficiently.
                </p>
              </div>
            </div>

            <div className="feature-list-item">
              <div className="feature-icon">
                <TrendingDown size={32} />
              </div>
              <div className="feature-content">
                <h3 className="heading-3">Enhanced Heat Transfer</h3>
                <p className="body-medium">
                  By increasing Hydrogen/Oxygen bonds and optimizing their vibration, OH4 enhances water's natural heat transfer properties, reducing fuel consumption by 13-31%.
                </p>
              </div>
            </div>

            <div className="feature-list-item">
              <div className="feature-icon">
                <Clock size={32} />
              </div>
              <div className="feature-content">
                <h3 className="heading-3">Set and Forget</h3>
                <p className="body-medium">
                  Once installed on your pipework, OH4 works indefinitely with zero maintenance, zero ongoing costs, and no parasitic load. Lasts 10+ years.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="section bg-section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Key Features</h2>
          </div>

          <div className="ai-grid">
            {oh4Features.map((feature, index) => (
              <div key={index} className="product-card">
                <h3 className="product-card-title">{feature.title}</h3>
                <p className="product-card-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Ideal Applications</h2>
          </div>

          <div className="applications-grid">
            <div className="application-item">
              <CheckCircle2 size={24} />
              <div>
                <h3 className="heading-3">Closed Water Heating Systems</h3>
                <p className="body-small">Works universally on water-based heating systems - boilers, heat pumps, any make or model</p>
              </div>
            </div>
            <div className="application-item">
              <CheckCircle2 size={24} />
              <div>
                <h3 className="heading-3">Ground Source Heat Pumps</h3>
                <p className="body-small">27% fuel saving proven in independent testing by JC Services</p>
              </div>
            </div>
            <div className="application-item">
              <CheckCircle2 size={24} />
              <div>
                <h3 className="heading-3">Air Source Heat Pumps</h3>
                <p className="body-small">Factory testing showed 31% fuel saving over 3-week period</p>
              </div>
            </div>
            <div className="application-item">
              <CheckCircle2 size={24} />
              <div>
                <h3 className="heading-3">All Pipework Types</h3>
                <p className="body-small">Works on plastic, copper, and steel pipework in closed loop systems</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Savings Calculator CTA */}
      <section className="section">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-2">Calculate Your Savings</h2>
            <p className="section-subtitle">
              Use our interactive calculator to estimate your OH4 energy savings and payback time.
            </p>
          </div>
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            <a href="/oh4/calculator.html" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Savings Calculator
              <ArrowRight size={20} />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="heading-2">See OH4 in action</h2>
            <p className="body-large">
              Contact us for a technical consultation and ROI assessment for your facility.
            </p>
            <Link to="/contact" className="btn-primary">
              Get Started
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OH4;
